import { cn } from '@/lib/utils';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useKeyboardStore } from '../stores/keyboard';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface SafeAreaContextValue {
  insets: SafeAreaInsets;
  isLoading: boolean;
}

const SafeAreaContext = createContext<SafeAreaContextValue | undefined>(
  undefined,
);

export function SafeAreaProvider({
  children,
  isEnabled,
}: {
  children: ReactNode;
  isEnabled: boolean;
}) {
  const [insets, setInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen: isKeyboardOpen } = useKeyboardStore();

  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    SafeArea.getSafeAreaInsets()
      .then(({ insets: safeAreaInsets }) => {
        setInsets(safeAreaInsets);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to get safe area insets:', error);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    // Set CSS custom properties on the root element
    const root = document.documentElement;
    const bottomInset = isKeyboardOpen
      ? 0
      : insets.bottom + (isNative ? 0 : 16);

    root.style.setProperty(
      '--safe-area-top',
      `${insets.top + (isNative ? 0 : 16)}px`,
    );
    root.style.setProperty('--safe-area-bottom', `${bottomInset}px`);
    root.style.setProperty('--safe-area-left', `${insets.left}px`);
    root.style.setProperty('--safe-area-right', `${insets.right}px`);
  }, [insets, isNative, isKeyboardOpen]);

  const effectiveBottom = isKeyboardOpen
    ? 0
    : insets.bottom + (isNative ? 0 : 16);

  if (!isEnabled) {
    return <>{children}</>;
  }

  return (
    <SafeAreaContext.Provider value={{ insets, isLoading }}>
      <div
        className={cn('h-screen w-screen md:py-0!', isNative && 'is-mobile')}
        style={{
          paddingTop: insets.top + (isNative ? 0 : 16),
          paddingBottom: effectiveBottom,
          paddingLeft: insets.left,
          paddingRight: insets.right,
        }}>
        {children}
      </div>
    </SafeAreaContext.Provider>
  );
}

export function useSafeArea() {
  const context = useContext(SafeAreaContext);
  if (context === undefined) {
    throw new Error('useSafeArea must be used within a SafeAreaProvider');
  }
  return context;
}
